import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import Editor from "@toast-ui/editor"
import "@toast-ui/editor/dist/i18n/ko-kr"

const BLOB_URL_TEMPLATE = "/rails/active_storage/blobs/redirect/:signed_id/:filename"

export default class extends Controller {
  static values = {
    height: { type: String, default: "420px" },
    placeholder: String,
    toolbar: { type: String, default: "default" },
    uploadError: String,
    defaultImageAlt: String
  }

  static targets = ["editor", "input", "imageInput", "fileInput"]

  connect() {
    this.cleanupHandlers = []

    if (this.isKmongToolbar()) {
      this.connectKmongEditor()
      return
    }

    const initialValue = this.normalizedInitialValue()

    this.editor = new Editor({
      el: this.editorTarget,
      height: this.heightValue,
      initialEditType: "wysiwyg",
      hideModeSwitch: true,
      language: "ko-KR",
      placeholder: this.placeholderValue || "",
      usageStatistics: false,
      toolbarItems: this.toolbarItems(),
      initialValue,
      hooks: {
        addImageBlobHook: async (blob, callback) => {
          const attachment = await this.uploadBlob(blob)
          callback(attachment.url, blob.name || attachment.filename)
        }
      }
    })

    this.form = this.element.closest("form")
    this.boundSync = this.sync.bind(this)
    this.boundSetValue = this.setValueFromEvent.bind(this)
    this.boundUpdateHeadingLabel = this.updateHeadingLabelFromSelection.bind(this)
    this.editor.on("change", this.boundSync)
    this.inputTarget.addEventListener("toast-editor:set-value", this.boundSetValue)
    this.inputTarget.addEventListener("toast-editor:sync", this.boundSync)
    this.form?.addEventListener("submit", this.boundSync)
    document.addEventListener("selectionchange", this.boundUpdateHeadingLabel)
    this.editorTarget.addEventListener("keyup", this.boundUpdateHeadingLabel)
    this.editorTarget.addEventListener("click", this.boundUpdateHeadingLabel)
    this.sync()
    this.updateHeadingLabelFromSelection()
  }

  disconnect() {
    this.cleanupHandlers?.forEach((cleanup) => cleanup())
    this.cleanupHandlers = []
    this.editor?.off("change", this.boundSync)
    this.inputTarget.removeEventListener("toast-editor:set-value", this.boundSetValue)
    this.inputTarget.removeEventListener("toast-editor:sync", this.boundSync)
    this.form?.removeEventListener("submit", this.boundSync)
    document.removeEventListener("selectionchange", this.boundUpdateHeadingLabel)
    this.editorTarget.removeEventListener("keyup", this.boundUpdateHeadingLabel)
    this.editorTarget.removeEventListener("click", this.boundUpdateHeadingLabel)
    this.editorTarget.removeEventListener("mouseup", this.boundUpdateHeadingLabel)
    this.editorTarget.removeEventListener("input", this.boundSync)
    this.editorTarget.removeEventListener("paste", this.boundHandleKmongPaste, true)
    this.editor?.destroy()
  }

  connectKmongEditor() {
    const initialValue = this.normalizedInitialValue()

    this.form = this.element.closest("form")
    this.boundSync = this.sync.bind(this)
    this.boundSetValue = this.setValueFromEvent.bind(this)
    this.boundUpdateHeadingLabel = this.updateHeadingLabelFromSelection.bind(this)
    this.boundHandleKmongPaste = this.handleKmongPaste.bind(this)

    this.editorTarget.innerHTML = ""
    this.editorTarget.classList.add("kmong-editor")

    this.kmongToolbar = document.createElement("div")
    this.kmongToolbar.className = "kmong-editor__toolbar"
    this.renderKmongToolbar()

    this.kmongEditable = document.createElement("div")
    this.kmongEditable.className = "kmong-editor__body toastui-editor-contents"
    this.kmongEditable.contentEditable = "true"
    this.kmongEditable.spellcheck = true
    this.kmongEditable.dataset.placeholder = this.placeholderValue || ""
    this.kmongEditable.style.height = `calc(${this.heightValue} - 47px)`
    this.kmongEditable.innerHTML = initialValue || "<p><br></p>"

    this.editorTarget.appendChild(this.kmongToolbar)
    this.editorTarget.appendChild(this.kmongEditable)

    document.addEventListener("selectionchange", this.boundUpdateHeadingLabel)
    this.editorTarget.addEventListener("click", this.boundUpdateHeadingLabel)
    this.editorTarget.addEventListener("keyup", this.boundUpdateHeadingLabel)
    this.editorTarget.addEventListener("mouseup", this.boundUpdateHeadingLabel)
    this.editorTarget.addEventListener("input", this.boundSync)
    this.editorTarget.addEventListener("paste", this.boundHandleKmongPaste, true)
    this.inputTarget.addEventListener("toast-editor:set-value", this.boundSetValue)
    this.inputTarget.addEventListener("toast-editor:sync", this.boundSync)
    this.form?.addEventListener("submit", this.boundSync)

    this.sync()
    this.updateHeadingLabelFromSelection()
  }

  renderKmongToolbar() {
    this.toolbarItems().forEach((group, index) => {
      if (index > 0) {
        const divider = document.createElement("span")
        divider.className = "kmong-editor__divider"
        this.kmongToolbar.appendChild(divider)
      }

      const groupElement = document.createElement("span")
      groupElement.className = "kmong-editor__toolbar-group"
      group.forEach((item) => groupElement.appendChild(item.el))
      this.kmongToolbar.appendChild(groupElement)
    })
  }

  openImagePicker() {
    this.imageInputTarget.click()
  }

  openFilePicker() {
    this.fileInputTarget.click()
  }

  async attachImage(event) {
    const [file] = event.target.files
    if (!file) return

    try {
      const attachment = await this.uploadBlob(file)
      const alt = this.escapeHtml(file.name || this.defaultImageAltValue || "첨부 이미지")
      this.appendHtml(`<p><img src="${attachment.url}" alt="${alt}"></p>`)
      this.sync()
    } finally {
      event.target.value = ""
    }
  }

  async attachFile(event) {
    const [file] = event.target.files
    if (!file) return

    try {
      const attachment = await this.uploadBlob(file)
      const filename = this.escapeHtml(file.name || attachment.filename)
      this.appendHtml(`<p><a href="${attachment.url}" target="_blank" rel="noopener">${filename}</a></p>`)
      this.sync()
    } finally {
      event.target.value = ""
    }
  }

  setValueFromEvent(event) {
    this.setValue(event.detail?.value || "")
  }

  setValue(value) {
    const html = value || ""
    this.inputTarget.value = html

    if (this.isKmongToolbar()) {
      if (this.kmongEditable) this.kmongEditable.innerHTML = html || "<p><br></p>"
      this.sync()
      return
    }

    if (this.editor) {
      this.editor.setHTML(html || "<p><br></p>", false)
      this.sync()
    }
  }

  sync() {
    if (this.isKmongToolbar()) {
      const html = this.normalizeKmongHtml(this.kmongEditable?.innerHTML || "")
      this.inputTarget.value = html
      this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
      this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
      return
    }

    const html = this.normalizeToastHtml(this.editor.getHTML())
    this.inputTarget.value = html
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  normalizeToastHtml(html) {
    if (!html) return ""

    const template = document.createElement("template")
    template.innerHTML = html
    template.content.querySelectorAll(".placeholder, .ProseMirror-widget").forEach((node) => node.remove())

    const normalized = template.innerHTML.trim()
    const text = template.content.textContent.replace(/\u200B/g, "").trim()
    const hasMediaOrStructure = template.content.querySelector("img, a, table, hr, pre, code, ul, ol, blockquote")
    if (!text && !hasMediaOrStructure) return ""

    return normalized
  }

  handleKmongPaste(event) {
    if (!this.isKmongToolbar()) return

    const clipboard = event.clipboardData
    const html = clipboard?.getData("text/html") || ""
    const text = clipboard?.getData("text/plain") || ""
    if (!text.trim() && !html.trim()) return

    event.preventDefault()
    event.stopImmediatePropagation()

    if (html.trim() && this.clipboardHtmlHasMeaningfulStyles(html)) {
      this.insertKmongHtmlAtSelection(this.normalizeKmongHtml(html))
    } else {
      this.insertKmongHtmlAtSelection(this.kmongPlainTextToHtml(text))
    }

    this.sync()
    this.updateHeadingLabelFromSelection()
  }

  clipboardHtmlHasMeaningfulStyles(html) {
    return /font-size\s*:|<(h[1-6]|strong|b|em|i|u)\b/i.test(html)
  }

  toolbarItems() {
    if (this.isKmongToolbar()) {
      return [
        [
          this.headingToolbarButton()
        ],
        [
          this.customToolbarButton({ name: "bold", tooltip: "굵게", html: "<strong>B</strong>", editorCommand: "bold" }),
          this.customToolbarButton({ name: "italic", tooltip: "기울임", html: "<em>I</em>", editorCommand: "italic" }),
          this.customToolbarButton({ name: "underline", tooltip: "밑줄", html: "<u>U</u>", domCommand: "underline" })
        ],
        [
          this.customToolbarButton({ name: "align-left", tooltip: "왼쪽 정렬", html: this.alignIcon("left"), domCommand: "justifyLeft" }),
          this.customToolbarButton({ name: "align-center", tooltip: "가운데 정렬", html: this.alignIcon("center"), domCommand: "justifyCenter" }),
          this.customToolbarButton({ name: "align-right", tooltip: "오른쪽 정렬", html: this.alignIcon("right"), domCommand: "justifyRight" }),
          this.customToolbarButton({ name: "align-justify", tooltip: "양쪽 정렬", html: this.alignIcon("justify"), domCommand: "justifyFull" })
        ]
      ]
    }

    return [
      ['heading', 'bold', 'italic', 'strike'],
      ['hr', 'quote'],
      ['ul', 'ol', 'task', 'indent', 'outdent'],
      ['image', 'table', 'link'],
      ['code', 'codeblock']
    ]
  }

  isKmongToolbar() {
    return this.toolbarValue === "kmong"
  }

  normalizedInitialValue() {
    const value = this.inputTarget.value
    if (!this.isKmongToolbar()) return value

    if (value && !this.htmlHasElements(value)) {
      const normalizedPlainText = this.kmongPlainTextToHtml(value)
      this.inputTarget.value = normalizedPlainText
      return normalizedPlainText
    }

    const normalizedHtml = this.normalizeKmongHtml(value)
    this.inputTarget.value = normalizedHtml
    return normalizedHtml
  }

  htmlHasElements(value) {
    return /<\/?[a-z][\s\S]*>/i.test(value)
  }

  normalizeKmongHtml(html) {
    if (!html || !/(font-size\s*:|<\/?(ol|ul|li|h1|h2|h3|h4|h5|h6|blockquote)\b)/i.test(html)) return html

    const template = document.createElement("template")
    template.innerHTML = html

    template.content.querySelectorAll("h1, h2").forEach((heading) => {
      heading.replaceWith(this.kmongStyledParagraphFrom(heading, "24px", true))
    })

    template.content.querySelectorAll("h3, h4, h5, h6").forEach((heading) => {
      heading.replaceWith(this.kmongStyledParagraphFrom(heading, "20px", true))
    })

    template.content.querySelectorAll("blockquote").forEach((quote) => {
      const fragment = document.createDocumentFragment()
      const children = Array.from(quote.children)

      if (children.length === 0) {
        fragment.appendChild(this.kmongStyledParagraphFrom(quote, "14px", false))
      } else {
        children.forEach((child) => {
          fragment.appendChild(this.kmongStyledParagraphFrom(child, "14px", false))
        })
      }

      quote.replaceWith(fragment)
    })

    template.content.querySelectorAll("ol, ul").forEach((list) => {
      const fragment = document.createDocumentFragment()
      const isOrdered = list.tagName.toLowerCase() === "ol"

      Array.from(list.children).forEach((item, index) => {
        if (item.tagName.toLowerCase() !== "li") return

        const paragraph = document.createElement("p")
        paragraph.appendChild(document.createTextNode(isOrdered ? `${index + 1}. ` : "• "))

        while (item.firstChild) {
          paragraph.appendChild(item.firstChild)
        }

        fragment.appendChild(paragraph)
      })

      list.replaceWith(fragment)
    })

    template.content.querySelectorAll("li").forEach((item) => {
      const paragraph = document.createElement("p")
      while (item.firstChild) {
        paragraph.appendChild(item.firstChild)
      }
      item.replaceWith(paragraph)
    })

    return template.innerHTML
  }

  kmongPlainTextToHtml(text) {
    const lines = text.replace(/\r\n?/g, "\n").split("\n")
    const firstContentIndex = lines.findIndex((line) => line.trim().length > 0)

    return lines.map((line, index) => {
      const trimmed = line.trim()
      if (!trimmed) return "<p><br></p>"

      if (index === firstContentIndex) {
        return this.kmongStyledParagraphHtml(trimmed, "24px", true)
      }

      if (/^\[[^\]]+\]$/.test(trimmed)) {
        return this.kmongStyledParagraphHtml(trimmed, "20px", true)
      }

      return `<p>${this.escapeHtml(line)}</p>`
    }).join("")
  }

  kmongStyledParagraphHtml(text, fontSize, bold) {
    const content = this.escapeHtml(text)
    const inner = bold ? `<strong>${content}</strong>` : content
    return `<p><span style="font-size: ${fontSize};">${inner}</span></p>`
  }

  insertKmongHtmlAtSelection(html) {
    if (!this.kmongEditable) return

    this.restoreKmongSelection()
    this.kmongEditable.focus()

    const selection = window.getSelection()
    if (!selection) return

    const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : document.createRange()
    if (!this.kmongEditable.contains(range.commonAncestorContainer)) {
      range.selectNodeContents(this.kmongEditable)
      range.collapse(false)
    }

    range.deleteContents()

    const container = document.createElement("div")
    container.innerHTML = html

    const fragment = document.createDocumentFragment()
    let lastNode = null
    while (container.firstChild) {
      lastNode = fragment.appendChild(container.firstChild)
    }

    range.insertNode(fragment)

    if (lastNode) {
      range.setStartAfter(lastNode)
      range.collapse(true)
      selection.removeAllRanges()
      selection.addRange(range)
      this.saveKmongSelection()
    }
  }

  kmongStyledParagraphFrom(node, fontSize, bold) {
    const paragraph = document.createElement("p")
    const span = document.createElement("span")
    span.setAttribute("style", `font-size: ${fontSize};`)

    const contentTarget = bold ? document.createElement("strong") : span
    while (node.firstChild) {
      contentTarget.appendChild(node.firstChild)
    }

    if (bold) {
      span.appendChild(contentTarget)
    }

    paragraph.appendChild(span)
    return paragraph
  }

  customToolbarButton({ name, tooltip, html, className = "", editorCommand = null, domCommand = null, payload = undefined }) {
    const button = document.createElement("button")
    button.type = "button"
    button.className = `toast-editor-custom-button toast-editor-custom-button--${name} ${className}`.trim()
    button.innerHTML = html
    button.setAttribute("aria-label", tooltip)
    if (name === "bold") this.boldToolbarButton = button
    button.addEventListener("mousedown", (event) => {
      event.preventDefault()
      this.saveKmongSelection()
    })
    button.addEventListener("click", (event) => {
      event.preventDefault()

      if (this.isKmongToolbar()) {
        this.restoreKmongSelection()
        this.kmongEditable?.focus()
        document.execCommand(domCommand || editorCommand, false)
        this.saveKmongSelection()
        this.sync()
        this.updateHeadingLabelFromSelection()
        return
      }

      this.editor?.focus()
      if (editorCommand) {
        this.editor?.exec(editorCommand, payload)
      } else if (domCommand) {
        document.execCommand(domCommand, false)
      }
      this.sync()
    })

    return { name, tooltip, el: button }
  }

  headingToolbarButton() {
    const wrapper = document.createElement("span")
    wrapper.className = "toast-editor-heading-menu"

    const button = document.createElement("button")
    button.type = "button"
    button.className = "toast-editor-custom-button toast-editor-custom-button--heading"
    button.innerHTML = `${this.headingIcon()}<span data-heading-label>본문</span>${this.chevronIcon()}`
    button.setAttribute("aria-label", "문단 스타일")
    button.setAttribute("aria-haspopup", "true")
    button.setAttribute("aria-expanded", "false")
    this.headingLabelElement = button.querySelector("[data-heading-label]")

    const menu = document.createElement("span")
    menu.className = "toast-editor-heading-menu__panel"
    menu.hidden = true

    this.headingOptions().forEach((option) => {
      const item = document.createElement("button")
      item.type = "button"
      item.className = "toast-editor-heading-menu__item"
      item.textContent = option.label
      item.dataset.headingValue = option.value
      item.addEventListener("mousedown", (event) => {
        event.preventDefault()
        this.saveKmongSelection()
      })
      item.addEventListener("click", (event) => {
        event.preventDefault()
        this.applyHeadingOption(option)
        button.querySelector("[data-heading-label]").textContent = option.label
        this.closeHeadingMenu(menu, button)
      })
      menu.appendChild(item)
    })
    this.headingMenuItems = Array.from(menu.querySelectorAll("[data-heading-value]"))

    button.addEventListener("mousedown", (event) => {
      event.preventDefault()
      this.saveKmongSelection()
    })
    button.addEventListener("click", (event) => {
      event.preventDefault()
      menu.hidden ? this.openHeadingMenu(menu, button) : this.closeHeadingMenu(menu, button)
    })

    const closeOnOutsideClick = (event) => {
      if (wrapper.contains(event.target)) return
      this.closeHeadingMenu(menu, button)
    }
    const closeOnEscape = (event) => {
      if (event.key === "Escape") this.closeHeadingMenu(menu, button)
    }
    document.addEventListener("click", closeOnOutsideClick)
    document.addEventListener("keydown", closeOnEscape)
    this.cleanupHandlers.push(() => {
      document.removeEventListener("click", closeOnOutsideClick)
      document.removeEventListener("keydown", closeOnEscape)
    })

    wrapper.appendChild(button)
    wrapper.appendChild(menu)

    return { name: "heading-style", tooltip: "문단 스타일", el: wrapper }
  }

  headingOptions() {
    return [
      { label: "대제목", value: "heading-2" },
      { label: "소제목", value: "heading-3" },
      { label: "본문", value: "paragraph" },
      { label: "주석", value: "quote" }
    ]
  }

  applyHeadingOption(option) {
    const applied = this.replaceKmongSelectionWithStyle(option)
    if (!applied) return

    this.setHeadingLabel(option.label, option.value)
    this.sync()
  }

  openHeadingMenu(menu, button) {
    this.saveKmongSelection()
    this.updateHeadingLabelFromSelection()
    menu.hidden = false
    button.setAttribute("aria-expanded", "true")
    button.querySelector(".toast-editor-chevron")?.classList.add("is-open")
  }

  closeHeadingMenu(menu, button) {
    menu.hidden = true
    button.setAttribute("aria-expanded", "false")
    button.querySelector(".toast-editor-chevron")?.classList.remove("is-open")
  }

  updateHeadingLabelFromSelection() {
    if (!this.isKmongToolbar() || !this.headingLabelElement) return

    this.saveKmongSelection()

    const selection = window.getSelection()
    const anchor = selection?.anchorNode
    if (!anchor || !this.editorTarget.contains(anchor)) return

    const element = anchor.nodeType === Node.ELEMENT_NODE ? anchor : anchor.parentElement
    const styleElement = element?.closest("span[style], h1, h2, h3, h4, h5, h6, blockquote, p")
    const option = this.headingOptionForElement(styleElement)
    if (option) this.setHeadingLabel(option.label, option.value)
    this.updateBoldToolbarState()
  }

  replaceKmongSelectionWithStyle(option) {
    const range = this.kmongStyleRange()
    if (!range) return false

    const text = range.toString()
    const replacementHtml = this.kmongStyleReplacementHtml(text, option)
    if (!replacementHtml) return false

    this.kmongEditable.focus()
    const selection = window.getSelection()
    selection.removeAllRanges()
    selection.addRange(range)
    this.insertKmongHtmlAtSelection(replacementHtml)
    return true
  }

  kmongStyleRange() {
    if (!this.kmongEditable) return null
    this.restoreKmongSelection()

    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return this.rangeForCurrentEditableBlock()

    const range = selection.getRangeAt(0)
    if (!this.kmongEditable.contains(range.commonAncestorContainer)) return null
    if (!range.collapsed) return range.cloneRange()

    return this.rangeForCurrentEditableBlock()
  }

  kmongStyleReplacementHtml(text, option) {
    const normalizedText = text.replace(/\r\n?/g, "\n")
    const lines = normalizedText.split("\n")
    if (lines.length <= 1) return this.kmongInlineStyleHtml(normalizedText, option)

    return lines.map((line) => {
      if (!line.trim()) return "<p><br></p>"
      return `<p>${this.kmongInlineStyleHtml(line, option)}</p>`
    }).join("")
  }

  kmongInlineStyleHtml(text, option) {
    const content = this.escapeHtml(text)

    switch (option.value) {
      case "heading-2":
        return `<span style="font-size: 24px;"><strong>${content}</strong></span>`
      case "heading-3":
        return `<span style="font-size: 20px;"><strong>${content}</strong></span>`
      case "quote":
        return `<span style="font-size: 14px;">${content}</span>`
      default:
        return `<span>${content}</span>`
    }
  }

  rangeForCurrentEditableBlock() {
    const selection = window.getSelection()
    const node = selection?.anchorNode
    if (!node || !this.kmongEditable?.contains(node)) return null

    const element = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement
    const block = element?.closest("p, div")
    if (!block || block === this.kmongEditable || !this.kmongEditable.contains(block)) return null

    const range = document.createRange()
    range.selectNodeContents(block)
    return range
  }

  saveKmongSelection() {
    if (!this.kmongEditable) return
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return

    const range = selection.getRangeAt(0)
    if (!this.kmongEditable.contains(range.commonAncestorContainer)) return
    this.savedKmongRange = range.cloneRange()
  }

  restoreKmongSelection() {
    if (!this.savedKmongRange) return
    const selection = window.getSelection()
    selection.removeAllRanges()
    selection.addRange(this.savedKmongRange)
  }

  headingOptionForElement(element) {
    if (!element) return this.headingOptions().find((option) => option.value === "paragraph")

    const tagName = element.tagName.toLowerCase()
    if (tagName === "h1" || tagName === "h2") return this.headingOptions().find((option) => option.value === "heading-2")
    if (/^h[3-6]$/.test(tagName)) return this.headingOptions().find((option) => option.value === "heading-3")
    if (tagName === "blockquote") return this.headingOptions().find((option) => option.value === "quote")

    const styled = element.closest("span[style]")
    const fontSize = styled ? this.normalizedFontSize(styled.style.fontSize) : ""
    if (fontSize === "24px") return this.headingOptions().find((option) => option.value === "heading-2")
    if (fontSize === "20px") return this.headingOptions().find((option) => option.value === "heading-3")
    if (fontSize === "14px") return this.headingOptions().find((option) => option.value === "quote")

    const computedElement = element.nodeType === Node.ELEMENT_NODE ? element : element.parentElement
    if (computedElement) {
      const computedStyle = window.getComputedStyle(computedElement)
      const computedSize = this.normalizedFontSize(computedStyle.fontSize)
      const computedWeight = Number.parseInt(computedStyle.fontWeight || "400", 10)
      if (computedSize === "24px" && computedWeight >= 600) return this.headingOptions().find((option) => option.value === "heading-2")
      if (computedSize === "20px" && computedWeight >= 600) return this.headingOptions().find((option) => option.value === "heading-3")
      if (computedSize === "14px") return this.headingOptions().find((option) => option.value === "quote")
    }

    return this.headingOptions().find((option) => option.value === "paragraph")
  }

  normalizedFontSize(fontSize) {
    const value = fontSize.toString().trim()
    if (value.endsWith("px")) return value
    if (value.endsWith("rem")) return `${Math.round(Number.parseFloat(value) * 16)}px`
    return value
  }

  setHeadingLabel(label, value) {
    if (this.headingLabelElement) this.headingLabelElement.textContent = label
    this.headingMenuItems?.forEach((item) => {
      const selected = item.dataset.headingValue === value
      item.classList.toggle("is-selected", selected)
      item.setAttribute("aria-selected", selected ? "true" : "false")
    })
    this.updateBoldToolbarState()
  }

  updateBoldToolbarState() {
    if (!this.boldToolbarButton) return

    const isBold = this.selectionHasBold()
    this.boldToolbarButton.classList.toggle("is-active", isBold)
    this.boldToolbarButton.setAttribute("aria-pressed", isBold ? "true" : "false")
  }

  selectionHasBold() {
    const selection = window.getSelection()
    const anchor = selection?.anchorNode
    if (!anchor || !this.editorTarget.contains(anchor)) return false

    if (document.queryCommandState("bold")) return true

    const element = anchor.nodeType === Node.ELEMENT_NODE ? anchor : anchor.parentElement
    const styledElement = element?.closest("strong, b, [style*='font-weight']")
    if (!styledElement || !this.editorTarget.contains(styledElement)) return false

    const fontWeight = styledElement.style?.fontWeight || window.getComputedStyle(styledElement).fontWeight
    if (fontWeight === "bold") return true

    return Number.parseInt(fontWeight || "400", 10) >= 600
  }

  headingIcon() {
    return '<span class="toast-editor-heading-icon" aria-hidden="true"><span>I</span><span>T</span></span>'
  }

  chevronIcon() {
    return '<svg class="toast-editor-chevron" viewBox="0 0 24 24" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>'
  }

  alignIcon(type) {
    const lineSets = {
      left: [18, 13, 18, 10],
      center: [16, 20, 16, 20],
      right: [18, 10, 18, 13],
      justify: [18, 18, 18, 18]
    }
    const [a, b, c, d] = lineSets[type]

    return `
      <svg class="toast-editor-align-icon" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4 7h${a}"/>
        <path d="M4 12h${b}"/>
        <path d="M4 17h${c}"/>
        <path d="M4 22h${d}"/>
      </svg>
    `
  }

  async uploadBlob(file) {
    const upload = new DirectUpload(file, this.inputTarget.dataset.directUploadUrl)

    return new Promise((resolve, reject) => {
      upload.create((error, attributes) => {
        if (error) {
          alert(this.uploadErrorValue || "파일 업로드에 실패했습니다.")
          reject(error)
          return
        }

        resolve({
          filename: attributes.filename,
          url: this.blobUrl(attributes.signed_id, attributes.filename)
        })
      })
    })
  }

  blobUrl(signedId, filename) {
    return BLOB_URL_TEMPLATE
      .replace(":signed_id", signedId)
      .replace(":filename", encodeURIComponent(filename))
  }

  escapeHtml(value) {
    return value
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }

  appendHtml(html) {
    if (this.isKmongToolbar()) {
      this.insertKmongHtmlAtSelection(html)
      return
    }

    const currentHtml = this.editor.getHTML()
    const baseHtml = currentHtml === "<p><br></p>" ? "" : currentHtml
    this.editor.setHTML(`${baseHtml}${html}`, false)
  }
}
